// Menu-item metrics table, ported from menu_test/products/items_list.coffee: the selected
// metric drives the single value column ('amount' is the respondents count + share bar).

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { processText } from '@aytm/app/stats/page/components/lib/utils'

import { formatSeconds } from '@aytm/app/stats/page/question-views/mn/lib/format'
import {
  buildHeaderTitle,
  calcSortedIndex,
  resolveClickable
} from '@aytm/app/stats/page/question-views/mn/lib/table-utils'

export const PRODUCT_RESPONSES_TITLES = {
  time: 'Time',
  pVolume: 'Quantity',
  pAmount: 'Amount',
  amount: 'Respondents'
}

const METRIC_COLUMN_WIDTH = '185px'

// each value row names the segment the chart draws for it, so hovering either echoes the other
const calcTraitTable = ({ id, viewMode, priceTemplate, trait, traitName, traitValues, answer, answerPosition }) => {
  const table = {
    id: `${id}-traits`,
    cols: [
      {
        type: 'title',
        title:
          (Object.hasOwn(trait, 'title') && trait.title) ||
          (Object.hasOwn(trait, 'id') && traitName.toUpperCase()) ||
          traitName
      }
    ],
    rows: traitValues.map((v, i) => {
      const a = answer[v.id] || {}
      const cells = [{ title: v.name, colorIndex: (i % 21) + 1 }]

      if ('amount' === viewMode) cells.push({ value: (a.percents || 0).toFixed(1) })

      const value = a[viewMode] || 0
      cells.push({
        value:
          'pAmount' === viewMode && priceTemplate
            ? priceTemplate.replace(/{{price}}/gi, value.toFixed(2))
            : ['pAmount', 'pVolume'].includes(viewMode)
              ? value.toFixed(2)
              : (() => {
                  const formatTraitValue = formatAmounts({ amount: value }, 'amount', 1)
                  // zero stays unit-less in the trait table, matching the original behaviour
                  return value && 'time' === viewMode ? formatSeconds(formatTraitValue) : formatTraitValue
                })()
      })

      return { cells, position: i + 1, pointId: `a${answerPosition}t${i + 1}` }
    }),
    classNames: ['child']
  }

  if ('amount' === viewMode) table.cols.push({ type: 'value', title: '%' })
  table.cols.push({ type: 'value', title: PRODUCT_RESPONSES_TITLES[viewMode] })

  return table
}

export const calcProductsTable = ({
  id,
  answer,
  answerVariants = [],
  viewMode,
  priceTemplate,
  format,
  trait,
  traitName,
  traitValues = [],
  isTraitType,
  viewClickable,
  percentDecimals,
  sortOrder,
  onSort,
  onLabelClick
} = {}) => {
  const clickable = resolveClickable({ format, viewClickable })
  const isLoading = 'loading' === answer
  const headerTitle = buildHeaderTitle({ answer, format })

  const props = {
    id,
    cols: [{ type: 'title', title: 'Menu items', sort: (aRow, bRow, dir) => dir * (aRow.position - bRow.position) }],
    onSort
  }

  if ('amount' === viewMode) {
    props.cols.push({
      type: 'progress',
      title: headerTitle('Respondents'),
      width: METRIC_COLUMN_WIDTH,
      percentDecimals,
      sort: (aRow, bRow, dir) =>
        dir * (aRow.cells[1].percents + aRow.position / 10000 - (bRow.cells[1].percents + bRow.position / 10000))
    })
  } else {
    props.cols.push({
      type: 'value',
      title: headerTitle(PRODUCT_RESPONSES_TITLES[viewMode] || 'Respondents'),
      width: METRIC_COLUMN_WIDTH,
      sort: (aRow, bRow, dir) =>
        dir * (aRow.cells[1].value + aRow.position / 10000 - (bRow.cells[1].value + bRow.position / 10000)),
      format: value => {
        if (['pAmount', 'pVolume'].includes(viewMode)) {
          const formatted = value.toFixed(2)
          return 'pAmount' === viewMode && priceTemplate ? priceTemplate.replace(/{{price}}/gi, formatted) : formatted
        }
        if ('time' === viewMode) return formatSeconds(formatAmounts({ amount: value }, 'amount', 1))
        return formatAmounts({ amount: value }, 'amount', 1)
      }
    })
  }

  props.rows = answerVariants
    .filter(av => !av.na) // the N/A menu item is dropped from per-item aggregates
    .map(av => {
      const as = answer?.[`a${av.position}`] || { amount: 0, percents: 0 }
      const value = as[viewMode] || 0

      const row = {
        cells: [
          {
            title: processText(av.text),
            label: `A${av.position}`,
            colorIndex: trait && isTraitType ? 0 : ((av.position - 1) % 21) + 1
          }
        ],
        position: av.position
      }

      row.cells.push(
        'amount' === viewMode
          ? { amount: value, percents: as.percents || 0, loading: isLoading }
          : { value, loading: isLoading }
      )

      if (Object.hasOwn(av, 'image')) row.cells[0].image = av.image
      if (clickable) row.cells[0].onLabelClick = onLabelClick

      if (trait && isTraitType) {
        const ta = as[traitName]
        if (undefined !== ta) {
          row.table = calcTraitTable({
            id,
            viewMode,
            priceTemplate,
            trait,
            traitName,
            traitValues,
            answer: ta,
            answerPosition: av.position
          })
        }
      }

      return row
    })

  calcSortedIndex(props.cols, sortOrder)

  return props
}
